/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { User } from 'firebase/auth';

// Initialize Firebase functions with emulator if in development
const functions = getFunctions();
// Uncomment the following line to use the Firebase emulator during local development
// if (process.env.NODE_ENV === 'development') {
//   connectFunctionsEmulator(functions, 'localhost', 5001);
// }

// Stripe publishable key:
const publishedKey = 'pk_test_51QaRTSDSTIkj3qOMFb6yhRXsPVNUvpsiSKu6bSHOnAGYQ2towKnLuAXw0UZocwOMNO9ZNZQxfGnQpjrO8YqTmuq900hce56HkH';
const stripePromise = loadStripe(publishedKey);

export enum SubscriptionTier {
  APPRENTICE = 'Apprentice',
  SMITH = 'Monster Smith',
  MASTERSMITH = 'Master Monster Smith'
}

export interface Subscription {
  subscription:    SubscriptionTier,
  status:          string,
  expiration:      Date,
  monstersAllowed: number
}

export interface SubscriptionOption {
  id: SubscriptionTier;
  name: string;
  price: string;
  monstersAllowed: number;
  description: string;
}

export const SUBSCRIPTION_OPTIONS: SubscriptionOption[] = [
  {
    id: SubscriptionTier.SMITH,
    name: "Monster Smith",
    price: "$9.99 / year",
    monstersAllowed: 50,
    description: "Store up to 50 monsters online, plus access to PC/NPC creator."
  },
  {
    id: SubscriptionTier.MASTERSMITH,
    name: "Master Monster Smith",
    price: "$19.99 / year",
    monstersAllowed: 500,
    description: "Store up to 500 monsters online, plus access to PC/NPC creator."
  }
];

class StripeService {
  async createCheckoutSession(user: User, selectedTier: SubscriptionTier): Promise<string | null> {
    try {
      if (!user) {
        throw new Error('User must be logged in to subscribe');
      }

      // Make sure the selectedTier is a valid string
      if (!selectedTier || typeof selectedTier !== 'string' || 
          (selectedTier !== SubscriptionTier.SMITH && 
           selectedTier !== SubscriptionTier.MASTERSMITH)) {
        throw new Error('Invalid subscription tier selected');
      }

      const idToken = await user.getIdToken();
      console.log('ID Token:', idToken); // Check if the token exists
  
      const createStripeCheckout = httpsCallable(functions, 'createStripeCheckout');
      
      // Pass the tier as a plain string
      const result = await createStripeCheckout({
        tier: selectedTier.toString()
      });
      
      // const functionUrl = "https://us-central1-monster-forge.cloudfunctions.net/createStripeCheckout";
      // const response = await fetch(functionUrl, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     "Authorization": `Bearer ${idToken}`, // 🔑 Attach Firebase Auth token manually
      //   },
      //   body: JSON.stringify({ tier: selectedTier.toString() }),
      // });
      // const result = await response.json();

      console.log("Stripe Checkout Response:", result);

      // The result should contain a sessionId
      if (!result.data || typeof result.data !== 'object') {
        throw new Error('Invalid response from server');
      }
      
      const data = result.data as any;
      if (!data.sessionId) {
        throw new Error('No session ID returned from server');
      }
      
      return data.sessionId;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  }
  
  async redirectToCheckout(sessionId: string): Promise<void> {
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }
      
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Error redirecting to checkout:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  async getCurrentSubscription(user: User): Promise<Subscription | null> {
    try {
      const getSubscriptionStatus = httpsCallable(functions, 'getSubscriptionStatus');
      
      const result = await getSubscriptionStatus({});
      const { subscription, status, expiration, monstersAllowed } = (result.data as any);
      
      return { subscription: subscription as SubscriptionTier, status, expiration, monstersAllowed };

    } catch (error) {
      console.error('Error getting subscription status:', error);
      return null;
    }
  }
  
  async cancelSubscription(user: User): Promise<boolean> {
    try {
      if (!user) {
        throw new Error('User must be logged in to cancel subscription');
      }

      const cancelStripeSubscription = httpsCallable(functions, 'cancelSubscription');
      
      const result = await cancelStripeSubscription({});
      const { success } = (result.data as any);
      
      return success;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return false;
    }
  }
}

export const stripeService = new StripeService();